/**
 * Leaflet Map Page - OpenStreetMap + Esri satellite with device markers and clustering.
 * Uses imperative Leaflet API for markers to prevent React re-render instability.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Skeleton } from 'antd';
import {
  MapContainer,
  TileLayer,
  useMap,
  LayersControl,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import AppLayout from '@/components/AppLayout';
import { MapSidePanel, type StatusFilter } from '@/components/map/MapSidePanel';
import { MapViewCard } from '@/components/map/MapViewCard';
import { useEffectiveUserId } from '@/hooks/useEffectiveUserId';
import { getProjects } from '@/services/projectService';
import { subscribeToViews, subscribeToLatestPhoto } from '@/services/viewService';
import { useGpsLocations } from '@/hooks/useGpsLocation';
import { useConnectionStatusFn } from '@/hooks/useConnectionStatus';
import type { Project, View, Photo } from '@/types';

function getCameraMode(view: View): 'internal' | 'dslr' {
  return view.settings?.camera?.mode ?? 'internal';
}

const DEFAULT_CENTER: [number, number] = [45.0, 10.0];
const DEFAULT_ZOOM = 6;

// Phone icon SVG path
const PHONE_PATH = 'M17,1H7A2,2,0,0,0,5,3V21a2,2,0,0,0,2,2H17a2,2,0,0,0,2-2V3A2,2,0,0,0,17,1ZM12,21a1,1,0,1,1,1-1A1,1,0,0,1,12,21ZM17,18H7V4H17Z';
const CAMERA_PATH = 'M12,15a3,3,0,1,0-3-3A3,3,0,0,0,12,15Zm7-9H16.17L14.58,4.42A2,2,0,0,0,13.17,4H10.83a2,2,0,0,0-1.41.59L7.83,6H5A2,2,0,0,0,3,8V18a2,2,0,0,0,2,2H19a2,2,0,0,0,2-2V8A2,2,0,0,0,19,6Zm-7,11a5,5,0,1,1,5-5A5,5,0,0,1,12,17Z';

// Cached marker icons
const iconCache = new Map<string, L.DivIcon>();
function getMarkerIcon(isOnline: boolean, isDslr: boolean): L.DivIcon {
  const key = `${isOnline}-${isDslr}`;
  if (iconCache.has(key)) return iconCache.get(key)!;
  const color = isOnline ? '#34A853' : '#EA4335';
  const iconPath = isDslr ? CAMERA_PATH : PHONE_PATH;
  const icon = L.divIcon({
    html: `<div style="width:26px;height:26px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path d="${iconPath}" fill="white"/></svg></div>`,
    className: '',
    iconSize: L.point(26, 26),
    iconAnchor: L.point(13, 13),
    popupAnchor: L.point(0, -13),
  });
  iconCache.set(key, icon);
  return icon;
}

// Custom cluster icon factory with color coding
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createClusterIcon(cluster: any) {
  const markers = cluster.getAllChildMarkers();
  const count = markers.length;
  let onlineCount = 0;
  let offlineCount = 0;
  for (const m of markers) {
    const opts = m.options as L.MarkerOptions & { isOnline?: boolean };
    if (opts.isOnline) onlineCount++;
    else offlineCount++;
  }
  let bgColor: string;
  if (offlineCount === 0) bgColor = '#34A853';
  else if (onlineCount === 0) bgColor = '#EA4335';
  else bgColor = '#F5A623';
  const size = count < 10 ? 28 : count < 100 ? 32 : 36;
  return L.divIcon({
    html: `<div style="background:${bgColor};color:white;border-radius:50%;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:11px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)">${count}</div>`,
    className: '',
    iconSize: L.point(size, size),
    iconAnchor: L.point(size / 2, size / 2),
  });
}

// Component to handle map programmatic moves
function MapController({ center, zoom, sidePanelOpen }: { center: [number, number] | null; zoom: number | null; sidePanelOpen: boolean }) {
  const map = useMap();
  useEffect(() => {
    if (center && zoom) {
      map.flyTo(center, zoom, { duration: 0.5 });
    }
  }, [center, zoom, map]);
  useEffect(() => {
    setTimeout(() => map.invalidateSize(), 300);
  }, [sidePanelOpen, map]);
  return null;
}

/**
 * Imperative marker cluster manager.
 * Creates and updates Leaflet markers directly, bypassing React re-render cycle.
 * This prevents popups from closing when Firestore real-time updates arrive.
 */
function ImperativeClusterLayer({
  views,
  latestPhotos,
  loadingViewIds,
  progressMessages,
  errors,
  onMarkerDblClick,
  openViewId,
  openPopupNonce,
}: {
  views: View[];
  latestPhotos: Record<string, Photo | null>;
  loadingViewIds: Set<string>;
  progressMessages: Map<string, string>;
  errors: Map<string, string>;
  onMarkerDblClick: (view: View) => void;
  openViewId: string | null;
  openPopupNonce: number;
}) {
  const map = useMap();
  // Drives the marker colours; re-created on each poll tick so markers turn offline
  // on their own rather than waiting for a views update.
  const connectionStatusFn = useConnectionStatusFn();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clusterGroupRef = useRef<any>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const popupRootsRef = useRef<Map<string, ReturnType<typeof createRoot>>>(new Map());

  // Create cluster group once
  useEffect(() => {
    const group = (L as any).markerClusterGroup({
      chunkedLoading: true,
      iconCreateFunction: createClusterIcon,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true,
      showCoverageOnHover: false,
      disableClusteringAtZoom: 16,
      animate: false,
      spiderfyDistanceMultiplier: 1.5,
    });
    clusterGroupRef.current = group;
    map.addLayer(group);

    return () => {
      map.removeLayer(group);
      // Cleanup React roots
      for (const root of popupRootsRef.current.values()) {
        root.unmount();
      }
      popupRootsRef.current.clear();
      markersRef.current.clear();
    };
  }, [map]);

  // Update markers when views change
  useEffect(() => {
    const group = clusterGroupRef.current;
    if (!group) return;

    const currentIds = new Set(views.filter(v => v.gpsLocation).map(v => v.viewId));
    const existingIds = new Set(markersRef.current.keys());

    // Remove markers for views no longer present
    for (const id of existingIds) {
      if (!currentIds.has(id)) {
        const marker = markersRef.current.get(id)!;
        group.removeLayer(marker);
        markersRef.current.delete(id);
        const root = popupRootsRef.current.get(id);
        if (root) { root.unmount(); popupRootsRef.current.delete(id); }
      }
    }

    // Add/update markers
    for (const view of views) {
      if (!view.gpsLocation) continue;
      const loc = view.gpsLocation;
      const status = connectionStatusFn(view.token, view.androidStatus);
      const isOnline = status === 'online';
      const isDslr = getCameraMode(view) === 'dslr';
      const icon = getMarkerIcon(isOnline, isDslr);

      let marker = markersRef.current.get(view.viewId);
      if (marker) {
        // Update existing marker icon (status may have changed)
        marker.setIcon(icon);
        (marker.options as any).isOnline = isOnline;
        // Update popup content
        updatePopupContent(view, status);
      } else {
        // Create new marker
        marker = L.marker([loc.lat, loc.lng], {
          icon,
          ...(({ isOnline }) as any),
        });

        // Create popup with React content
        const popupContainer = document.createElement('div');
        const root = createRoot(popupContainer);
        popupRootsRef.current.set(view.viewId, root);

        root.render(
          <MapViewCard
            view={view}
            latestPhoto={latestPhotos[view.viewId] ?? null}
            connectionStatus={status}
            isGpsLoading={loadingViewIds.has(view.viewId)}
            gpsProgressMessage={progressMessages.get(view.viewId)}
            gpsError={errors.get(view.viewId)}
          />
        );

        // Content has fixed width 220 + 8px padding = 228. Pin both min and max
        // so Leaflet doesn't pad an empty strip on the right.
        const popup = L.popup({ closeButton: true, autoPan: false, minWidth: 228, maxWidth: 228 })
          .setContent(popupContainer);
        marker.bindPopup(popup);

        marker.on('dblclick', (e: L.LeafletMouseEvent) => {
          L.DomEvent.stopPropagation(e.originalEvent);
          onMarkerDblClick(view);
        });

        group.addLayer(marker);
        markersRef.current.set(view.viewId, marker);
      }
    }
  }, [views, latestPhotos, loadingViewIds, progressMessages, errors, onMarkerDblClick, connectionStatusFn]);

  // Update popup content for existing markers
  function updatePopupContent(view: View, status: 'online' | 'offline' | 'survival' | 'unpaired' | 'never_connected') {
    const root = popupRootsRef.current.get(view.viewId);
    if (root) {
      root.render(
        <MapViewCard
          view={view}
          latestPhoto={latestPhotos[view.viewId] ?? null}
          connectionStatus={status}
          isGpsLoading={loadingViewIds.has(view.viewId)}
        />
      );
    }
  }

  // Open popup for specific view (from side panel click).
  // Depends on `openPopupNonce` so clicking the same view twice re-triggers
  // (state equality would otherwise skip the effect).
  useEffect(() => {
    if (!openViewId) return;
    const group = clusterGroupRef.current;
    const marker = markersRef.current.get(openViewId);
    if (!group || !marker) return;

    // If the marker is hidden inside a cluster, `openPopup()` is a no-op.
    // `zoomToShowLayer` zooms/spiderfies the cluster and fires the callback
    // once the marker is actually visible on the map.
    const openIt = () => {
      // Small delay lets the flyTo animation settle before binding popup.
      setTimeout(() => marker.openPopup(), 50);
    };
    if ((group as any).hasLayer && (group as any).hasLayer(marker) && !(map as any).hasLayer(marker)) {
      (group as any).zoomToShowLayer(marker, openIt);
    } else {
      openIt();
    }
  }, [openViewId, openPopupNonce, map]);

  return null;
}

export default function LeafletMapPage() {
  const { effectiveUid } = useEffectiveUserId();
  // Bound to the live config and re-created on each poll tick, so the memos below
  // recompute as devices age out instead of freezing at their last render.
  const connectionStatusFn = useConnectionStatusFn();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const focusViewId = searchParams.get('viewId');
  const focusProjectId = searchParams.get('projectId');

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [viewsByProject, setViewsByProject] = useState<Record<string, View[]>>({});
  const [latestPhotos, setLatestPhotos] = useState<Record<string, Photo | null>>({});
  const [loading, setLoading] = useState(true);
  // Safety valve for the loading gate: never hold the skeleton longer than 4s
  const [forceReveal, setForceReveal] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setForceReveal(true), 4000);
    return () => window.clearTimeout(t);
  }, []);

  // Filter state
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(focusProjectId);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Map state
  const [sidePanelOpen, setSidePanelOpen] = useState(true);
  const [mapTarget, setMapTarget] = useState<{ center: [number, number]; zoom: number } | null>(null);
  const [openPopupViewId, setOpenPopupViewId] = useState<string | null>(null);
  // Incremented on every side-panel click so the open-popup effect re-fires
  // even when the same view is clicked twice in a row.
  const [openPopupNonce, setOpenPopupNonce] = useState(0);
  const mapRef = useRef<L.Map | null>(null);

  // Flatten all views
  const allViews = useMemo(() => {
    return Object.values(viewsByProject).flat();
  }, [viewsByProject]);

  // Filter views
  const filteredViews = useMemo(() => {
    let views = allViews;
    if (selectedProjectId) {
      views = views.filter((v) => v.projectId === selectedProjectId);
    }
    if (statusFilter !== 'all') {
      views = views.filter((v) => {
        const status = connectionStatusFn(v.token, v.androidStatus);
        if (statusFilter === 'online') return status === 'online';
        return status === 'offline' || status === 'never_connected';
      });
    }
    return views;
  }, [allViews, selectedProjectId, statusFilter, connectionStatusFn]);

  // Views with GPS location
  const viewsWithLocation = useMemo(() => {
    return filteredViews.filter((v) => v.gpsLocation);
  }, [filteredViews]);

  // GPS hook
  const { loadingViewIds, errors: gpsErrors, progressMessages: gpsProgressMessages, isLoading: isGpsRefreshing, requestAll } =
    useGpsLocations(filteredViews);

  const getViewConnectionStatus = useCallback(
    (view: View) => connectionStatusFn(view.token, view.androidStatus),
    [connectionStatusFn]
  );

  // Load projects and subscribe to views
  useEffect(() => {
    if (!effectiveUid) return;
    let viewUnsubscribes: (() => void)[] = [];
    async function loadData() {
      const projectList = await getProjects(effectiveUid!);
      setProjects(projectList);
      viewUnsubscribes = projectList.map((project) =>
        subscribeToViews(effectiveUid!, project.projectId, (views) => {
          setViewsByProject((prev) => ({ ...prev, [project.projectId]: views }));
        })
      );
      setLoading(false);
    }
    loadData();
    return () => { viewUnsubscribes.forEach((unsub) => unsub()); };
  }, [effectiveUid]);

  // Subscribe to latest photos
  useEffect(() => {
    if (!effectiveUid) return;
    const photoUnsubscribes: (() => void)[] = [];
    for (const view of allViews) {
      const unsub = subscribeToLatestPhoto(effectiveUid, view.viewId, (photo) => {
        setLatestPhotos((prev) => ({ ...prev, [view.viewId]: photo }));
      });
      photoUnsubscribes.push(unsub);
    }
    return () => { photoUnsubscribes.forEach((unsub) => unsub()); };
  }, [effectiveUid, allViews.map((v) => v.viewId).join(',')]);

  // Focus on view/project from URL
  useEffect(() => {
    if (!mapRef.current || loading) return;
    if (focusViewId) {
      const view = allViews.find((v) => v.viewId === focusViewId);
      if (view?.gpsLocation) {
        setMapTarget({ center: [view.gpsLocation.lat, view.gpsLocation.lng], zoom: 18 });
        setOpenPopupViewId(focusViewId);
        setOpenPopupNonce((n) => n + 1);
      }
    } else if (focusProjectId) {
      fitBoundsToViews(viewsWithLocation.filter((v) => v.projectId === focusProjectId));
    }
  }, [focusViewId, focusProjectId, loading, viewsWithLocation.length]);

  const fitBoundsToViews = useCallback((views: View[]) => {
    if (!mapRef.current || views.length === 0) return;
    if (views.length === 1 && views[0].gpsLocation) {
      setMapTarget({ center: [views[0].gpsLocation.lat, views[0].gpsLocation.lng], zoom: 15 });
      return;
    }
    const bounds = L.latLngBounds(
      views.filter((v) => v.gpsLocation).map((v) => [v.gpsLocation!.lat, v.gpsLocation!.lng])
    );
    mapRef.current.fitBounds(bounds, { padding: [60, 60] });
  }, []);

  const handleViewClick = useCallback((view: View) => {
    if (!view.gpsLocation) return;
    setMapTarget({ center: [view.gpsLocation.lat, view.gpsLocation.lng], zoom: 18 });
    setOpenPopupViewId(view.viewId);
    // Bump nonce so clicking the same list item twice still re-opens the popup.
    setOpenPopupNonce((n) => n + 1);
  }, []);

  const handleMarkerDblClick = useCallback((view: View) => {
    navigate(`/photos/${view.viewId}`);
  }, [navigate]);

  const handleRefreshAll = useCallback(() => {
    requestAll();
  }, [requestAll]);

  // Single reveal: wait for the project list AND the first views snapshot of
  // every project, so markers/panel appear in one shot instead of dripping in.
  // The 4s valve only covers the added viewsReady signal, never `loading`.
  const viewsReady = projects.every((p) => viewsByProject[p.projectId] !== undefined);
  const pageReady = !loading && (viewsReady || forceReveal);

  if (!pageReady) {
    return (
      <AppLayout>
        {/* Skeleton mirrors the real layout: 280px side panel + map area */}
        <div style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
          <div
            style={{
              width: 280,
              flexShrink: 0,
              padding: 16,
              background: '#fff',
              borderRight: '1px solid #f0f0f0',
            }}
          >
            <Skeleton active title={false} paragraph={{ rows: 8 }} />
          </div>
          <div style={{ flex: 1, background: '#e9ecef' }} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
        <MapSidePanel
          projects={projects}
          views={filteredViews}
          selectedProjectId={selectedProjectId}
          statusFilter={statusFilter}
          onProjectChange={setSelectedProjectId}
          onStatusFilterChange={setStatusFilter}
          onViewClick={handleViewClick}
          onRefreshAll={handleRefreshAll}
          isRefreshing={isGpsRefreshing}
          getConnectionStatus={getViewConnectionStatus}
          loadingViewIds={loadingViewIds}
          collapsed={!sidePanelOpen}
          onToggle={() => setSidePanelOpen((prev) => !prev)}
        />

        <div style={{ flex: 1 }}>
          <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            style={{ width: '100%', height: '100%' }}
            minZoom={2}
            maxBounds={[[-85, -180], [85, 180]]}
            maxBoundsViscosity={1.0}
            attributionControl={false}
            doubleClickZoom={true}
            ref={mapRef}
          >
            <MapController
              center={mapTarget?.center ?? null}
              zoom={mapTarget?.zoom ?? null}
              sidePanelOpen={sidePanelOpen}
            />

            <LayersControl position="topright">
              <LayersControl.BaseLayer name="Satellite" checked>
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution="Tiles &copy; Esri"
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Street Map">
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Terrain">
                <TileLayer
                  url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
                  maxZoom={17}
                />
              </LayersControl.BaseLayer>
            </LayersControl>

            <ImperativeClusterLayer
              views={viewsWithLocation}
              latestPhotos={latestPhotos}
              loadingViewIds={loadingViewIds}
              progressMessages={gpsProgressMessages}
              errors={gpsErrors}
              onMarkerDblClick={handleMarkerDblClick}
              openViewId={openPopupViewId}
              openPopupNonce={openPopupNonce}
            />
          </MapContainer>
        </div>
      </div>
    </AppLayout>
  );
}
